/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import { useMemo, useRef, useCallback, useState, useEffect } from 'react';
import ProductDetailSpecificationV2 from '@/components/ProductDetail/ProductDetailSpecification/ProductDetailSpecificationV2';
import { STARCTypography } from '@/components/STARCTypography';
import { CMSBanner } from '@/features/contentstack/components/CMSBanner/CMSBanner';
import { CMSCertona } from '@/features/contentstack/components/CMSCertona/CMSCertona';
import {
  CMSCompleteTheJob,
  type CMSCompleteTheJobProps,
} from '@/features/contentstack/components/CMSCompleteTheJob/CMSCompleteTheJob';
import { CMSSpacer } from '@/features/contentstack/components/CMSSpacer/CMSSpacer';
import type {
  FormattedContentStackDefaultPDP,
  FormattedContentStackPremiumPDP,
  ContentStackCertona,
  ContentStackDefaultPdpAnchorBlock,
} from '@/features/contentstack/interface';
import { CMSSEOComparisonChart } from '@/features/contentstack/components/CMSSEOComparisonChart/CMSSEOComparisonChart';
import { CMSExperienceInjector } from '@/features/contentstack/components/CMSExperienceInjector/CMSExperienceInjector';
import { BuyersGuide } from '../BuyersGuide';
import { Accordion, Hidden, Text, View, useResize } from '@az/starc-ui';
import { CMSModularHandler as CMSLPModularHandler } from '../../../../features/landingPage/components/CMSModularHandler';
import { CMSPDPYmme } from './CMSPDPYmme';
import { BazaarVoice } from '../BazaarVoice';
import {
  ScrolledPartCardSkeleton,
  type CertonaBlockProps,
  type AddCertonaItemObjectType,
} from '@/features/certona';
import { CMSBreadcrumbs } from './CMSBreadcrumbs';
import { useFeatureFlag } from '@/features/globalConfig';
import { filterCTJBlocks } from '../../utils/filterCTJBlocks';
import { prioritizeLCP } from '../../utils/prioritizeLCP';
import { CustomReviews } from '@/features/customReviews/components/CustomReviews/CustomReviews';
import { useProductDetails } from '@/features/product/api/getProductDetails';
import { useMonetateDecisionFlag } from '@/features/kibo/api/getKiboDecision';
import { CMSTarget } from '@/features/contentstack/components/CMSTarget/CMSTarget';
import { useAcesFlags } from '@/features/ymme/utils/acesFlags';
import { BazaarVoiceQuestions } from '../BazaarVoiceQuestions';
import { useCertonaAddToCartFlag } from '@/hooks/useCertonaAddToCartCTAFlag';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useLocale } from '@/hooks/useLocale';
import { countryCodes } from '@/constants/locale';
import { useScrollIntoViewDOM } from '@/hooks/useScrollIntoView';
import { BuyAgainCandidatesWithFallback } from '@/components/ActionCandidatesWithFallback/BuyAgainCandidatesWithFallback/BuyAgainCandidatesWithFallback';
import ProductDetailSpecification, {
  type ProductDetailSpecificationProps,
} from '@/components/ProductDetail/ProductDetailSpecification';
import { useLabels } from '@/hooks/useLabels';
import styles from '../../styles.module.scss';
import { getCMSModularBlockId } from '@/features/contentstack/utils/getCMSModularBlockId';
import { CMSPencilBanner } from '@/features/contentstack/components/CMSPencilBanner/CMSPencilBanner';
import { CUSTOMER_REVIEWS_ID } from '@/features/customReviews/constants/sectionHash';
import { useForteDecisionFlagWithTrueDefault } from '@/features/forte/api/getForteDecision';

const labelMap = { label_reviews_customerReviews: 'label_reviews_customerReviews' };

type Props = {
  content:
    | FormattedContentStackDefaultPDP['pageBody']
    | FormattedContentStackPremiumPDP['pageBody'];
  ProductDetails: React.ReactNode;
  certonaType: 'accessoriesproduct' | 'pencilpdp' | 'pdp';
  'data-testid'?: string;
  canRenderComparisonChart: boolean;
  setCertonaRecommendationAddedToCart: React.Dispatch<React.SetStateAction<boolean>>;
  setAddedCertonaItemObject: React.Dispatch<React.SetStateAction<AddCertonaItemObjectType | null>>;
  oemPartNumber?: string;
  productId?: string;
  isPremiumPDPEnabled: boolean;
} & Pick<
  CertonaBlockProps,
  'certonaRecommendations' | 'onCertonaRecommendationClick' | 'setAddedCertonaItemObject'
> &
  ProductDetailSpecificationProps &
  CMSCompleteTheJobProps;

const CMSModularHandler = ({
  content,
  certonaRecommendations,
  onCertonaRecommendationClick,
  setAddedCertonaItemObject,
  ProductDetails,
  setShouldOpenSpecsPanel,
  setWarrantyModal,
  shouldOpenSpecsPanel,
  shouldShowLoadingSkeleton,
  warrantyModal,
  fulfillmentId,
  shippingType,
  canRenderComparisonChart,
  certonaType,
  setCertonaRecommendationAddedToCart,
  oemPartNumber,
  isPremiumPDPEnabled,
  ...props
}: Props) => {
  const { data: productDetails, isPending } = useProductDetails();
  const { acesEnabled, acesTransitionPhase } = useAcesFlags();
  const partTerminologyId = productDetails?.product?.partTerminologyId;
  const partGroupId = productDetails?.product?.partGroupId;
  const currentSkuId = productDetails?.product?.itemId;
  const locale = useLocale();
  const showBuyersGuideModal = (acesEnabled && !acesTransitionPhase) || !acesEnabled;
  const nonAppPartTypes = useMonetateDecisionFlag('nonAppPartTypes');
  const isPdpLowerV2Enabled = useForteDecisionFlagWithTrueDefault('pdpRedesignEnabled');
  const enableBuyAgainOnPlpAndPdp = useForteDecisionFlagWithTrueDefault(
    'enableBuyAgainOnPlpAndPdp',
  );
  const { originalSystemCode, originalPartTypeId } = productDetails?.product ?? {};
  const isNonAppItem = originalSystemCode === 10;
  const isPdpCertonaAddToCartEnabled = useCertonaAddToCartFlag();
  const isNonAppAffinityEnabled = useFeatureFlag('AFFINITY_NON_APP_ENABLED') === 'true';
  const isCustomReviewsEnabled = useFeatureFlag('CUSTOM_REVIEWS_ENABLED') === 'true';
  const buyAgainEndpointEnabled = useFeatureFlag('BUY_AGAIN_ENDPOINT_ENABLED') === 'true';
  const dateRange = Number(useFeatureFlag('BUY_AGAIN_DATE_RANGE'));
  const isDefaultPdpAnchorEnabled = useForteDecisionFlagWithTrueDefault(
    'defaultPdpAnchorBlocksEnabled',
  );
  const isProductDetailsPageV2Enabled = useForteDecisionFlagWithTrueDefault('pdpRedesignEnabled');
  const useNewPersonalizationEngine = useMonetateDecisionFlag('personalization_engine_v2');
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const isUS = locale === countryCodes.us;
  const { label_reviews_customerReviews } = useLabels(labelMap);

  const hasCustomCTJ = useMemo(() => {
    return content?.some((block) => block.completethejob?.taxonomy);
  }, [content]);

  const filteredContent = useMemo(() => {
    return prioritizeLCP(filterCTJBlocks(content ?? [], hasCustomCTJ));
  }, [content, hasCustomCTJ]);

  const { label_productChart_staticHeader } = useLabels({
    label_productChart_staticHeader: 'label_productChart_staticHeader',
  });

  useScrollIntoViewDOM();

  const partsProCtjConditions =
    !isNonAppItem ||
    (isNonAppItem &&
      isNonAppAffinityEnabled &&
      !!nonAppPartTypes &&
      !!originalPartTypeId &&
      // TODO: consider using catalog-configs instead of searching in the array
      nonAppPartTypes.includes(originalPartTypeId));

  const header = (
    <Text
      variant="large-body"
      weight="bold"
      attributes={{ style: { fontSize: isMobile ? '22px' : 'var(--st-unit-7)' } }}
    >
      {label_reviews_customerReviews}
    </Text>
  );

  const customReviewsRef = useRef<HTMLDivElement>(null);
  const accordionRef = useRef<HTMLDivElement>(null);

  const recalculateAccordionHeight = useCallback(() => {
    if (accordionRef.current && customReviewsRef.current) {
      const contentElement = accordionRef.current.querySelector(
        '[data-testid="st-accordion-content"]',
      ) as HTMLElement;
      if (contentElement) {
        contentElement.style.height = `${customReviewsRef.current.scrollHeight || 0}px`;
      }
    }
  }, []);

  useResize(customReviewsRef, recalculateAccordionHeight);

  const [isReviewsAccordionOpen, setIsReviewsAccordionOpen] = useState(true);

  // Listen for custom events to open accordion when navigating to customer reviews
  useEffect(() => {
    const handleOpenReviewsAccordion = () => {
      setIsReviewsAccordionOpen(true);
    };
    window.addEventListener('openReviewsAccordion', handleOpenReviewsAccordion);

    return () => {
      window.removeEventListener('openReviewsAccordion', handleOpenReviewsAccordion);
    };
  }, []);

  const accordionWrapper = (children: React.ReactNode) => {
    return (
      <View className={styles.accordion}>
        <div ref={accordionRef}>
          <Accordion
            headerOptions={{
              headerElement: header,
            }}
            open={isReviewsAccordionOpen}
            onToggle={(isOpen) => {
              setIsReviewsAccordionOpen(isOpen);
            }}
            className={styles.customerReviewsAccordion}
          >
            {children}
          </Accordion>
        </div>
      </View>
    );
  };

  return (
    <View attributes={{ 'data-testid': props['data-testid'] ?? '' }}>
      <STARCTypography>
        {filteredContent?.map((modularBlock, index) => {
          const getBlockId = (blockName: string) => getCMSModularBlockId(modularBlock, blockName);
          if (modularBlock.banner?.banner?.[0]?.asset_size) {
            const banner = modularBlock.banner.banner[0];
            const isPriorityBanner =
              banner?.image && 'priority' in banner.image && banner.image.priority;

            return (
              <div id={getBlockId('banner')} key={index}>
                <View padding={[4, 0]}>
                  <CMSBanner content={banner} priority={isPriorityBanner} />
                </View>
              </div>
            );
          }

          if (modularBlock.bazaar_voice_questions?.bazaar_voice_questions?.[0]?.enable) {
            return (
              <CMSSpacer
                key={index}
                removeVerticalSpacing
                id={getBlockId('bazaar_voice_questions')}
              >
                <BazaarVoiceQuestions />
              </CMSSpacer>
            );
          }

          if (modularBlock.buyers_guide_mobile?.enable && showBuyersGuideModal) {
            return (
              <div id={getBlockId('buyers_guide_mobile')} key={index}>
                <Hidden hide={{ m: true }}>
                  <BuyersGuide />
                </Hidden>
              </div>
            );
          }

          if (modularBlock.buyers_guide?.enable && showBuyersGuideModal) {
            return (
              <div id={getBlockId('buyers_guide')} key={index}>
                <Hidden hide={{ m: false, s: true }}>
                  <BuyersGuide />
                </Hidden>
              </div>
            );
          }

          if ('pdp_slot1' in modularBlock && modularBlock.pdp_slot1?.pdp_slot1) {
            const pdpSlotContent = modularBlock.pdp_slot1?.pdp_slot1?.[0]?.pdp_slot_content ?? [];
            const hasContent =
              pdpSlotContent.length > 0
                ? pdpSlotContent.some((block) => {
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                    const items = Object.values(
                      Object.values(block as Record<string, unknown>)[0] ?? {},
                    )[0];
                    return Array.isArray(items) && items.length > 0;
                  })
                : false;
            const showLabel = isPdpLowerV2Enabled && hasContent;
            return (
              <div id={getBlockId('pdp_slot1')} key={index}>
                <View>
                  {showLabel && (
                    <Text
                      as="h1"
                      variant="large-body"
                      weight="bold"
                      attributes={{ style: { fontSize: isMobile ? '22px' : 'var(--st-unit-7)' } }}
                      className={styles.fromTheBrandTitle}
                    >
                      {label_productChart_staticHeader}
                    </Text>
                  )}
                  <CMSLPModularHandler
                    content={modularBlock.pdp_slot1.pdp_slot1[0].pdp_slot_content}
                  />
                </View>
              </div>
            );
          }

          if ('pdp_slot2' in modularBlock && modularBlock.pdp_slot2?.pdp_slot2) {
            return (
              <div id={getBlockId('pdp_slot2')} key={index}>
                <CMSLPModularHandler
                  content={modularBlock.pdp_slot2.pdp_slot2[0].pdp_slot_content}
                />
              </div>
            );
          }

          if (isDefaultPdpAnchorEnabled && !isPremiumPDPEnabled && 'anchor_top' in modularBlock) {
            const anchorBlock = modularBlock as ContentStackDefaultPdpAnchorBlock;
            const content = anchorBlock.anchor_top?.content;
            if (content?.length) {
              return (
                <div id={getBlockId('anchor_top')} key={index}>
                  <CMSLPModularHandler content={content} />
                </div>
              );
            }
          }

          if (
            isDefaultPdpAnchorEnabled &&
            !isPremiumPDPEnabled &&
            'anchor_middle' in modularBlock
          ) {
            const anchorBlock = modularBlock as ContentStackDefaultPdpAnchorBlock;
            const content = anchorBlock.anchor_middle?.content;
            if (content?.length) {
              return (
                <div id={getBlockId('anchor_middle')} key={index}>
                  <CMSLPModularHandler content={content} />
                </div>
              );
            }
          }

          if (
            isDefaultPdpAnchorEnabled &&
            !isPremiumPDPEnabled &&
            'anchor_bottom' in modularBlock
          ) {
            const anchorBlock = modularBlock as ContentStackDefaultPdpAnchorBlock;
            const content = anchorBlock.anchor_bottom?.content;
            if (content?.length) {
              return (
                <div id={getBlockId('anchor_bottom')} key={index}>
                  <CMSLPModularHandler content={content} />
                </div>
              );
            }
          }

          if (
            'experience_injector' in modularBlock &&
            modularBlock.experience_injector?.experience_injector
          ) {
            return (
              <CMSSpacer key={index} removeVerticalSpacing id={getBlockId('experience_injector')}>
                <CMSExperienceInjector
                  key={index}
                  content={modularBlock.experience_injector.experience_injector[0]}
                />
              </CMSSpacer>
            );
          }

          if (modularBlock.ymme?.enable) {
            return (
              <CMSSpacer key={index} removeVerticalSpacing id={getBlockId('ymme')}>
                <CMSPDPYmme
                  shouldShowLoadingSkeleton={shouldShowLoadingSkeleton}
                  certonaRecommendations={certonaRecommendations}
                  setCertonaRecommendationAddedToCart={setCertonaRecommendationAddedToCart}
                  setAddedCertonaItemObject={setAddedCertonaItemObject}
                />
              </CMSSpacer>
            );
          }

          if (modularBlock.bazaarvoice?.enable) {
            if (isCustomReviewsEnabled) {
              return (
                <CMSSpacer
                  removeHorizontalSpacing={{
                    s: !isPdpLowerV2Enabled,
                    m: isPdpLowerV2Enabled,
                    l: isPdpLowerV2Enabled,
                  }}
                  removeVerticalSpacing={isPdpLowerV2Enabled}
                  key={index}
                  id={isPdpLowerV2Enabled ? CUSTOMER_REVIEWS_ID : 'bv-pdp-container'}
                >
                  {isPdpLowerV2Enabled ? (
                    accordionWrapper(
                      <CustomReviews
                        showHeader={false}
                        containerRef={customReviewsRef}
                        onContentLoaded={recalculateAccordionHeight}
                      />,
                    )
                  ) : (
                    <CustomReviews />
                  )}
                  <div
                    id="qualtrics-pdp-embedded-feedback"
                    className={styles.qualtricsEmbeddedFeedback}
                  />
                </CMSSpacer>
              );
            }

            if (isUS) {
              return (
                <CMSSpacer removeHorizontalSpacing={{ s: true }} key={index}>
                  {isPdpLowerV2Enabled ? accordionWrapper(<BazaarVoice />) : <BazaarVoice />}
                </CMSSpacer>
              );
            }
          }

          const certonaContent =
            modularBlock.certona_middle_content?.certona?.[0] ??
            modularBlock.certona_bottom_content?.certona?.[0] ??
            modularBlock.certona_content_2?.certona?.[0];

          const certonaMiddleOos =
            modularBlock.certona_middle_content?.certona?.[0]?.container_id &&
            modularBlock.certona_middle_content.certona[0]?.container_id.includes(
              'accessoriesproduct',
            )
              ? 'pencilproduct2_rr'
              : modularBlock.certona_middle_content?.certona?.[0];

          const certonaBottomOos =
            modularBlock.certona_content_2?.certona?.[0].container_id &&
            !modularBlock.certona_content_2.certona[0]?.container_id.includes('accessoriesproduct')
              ? 'pencilproduct3_rr'
              : modularBlock.certona_content_2?.certona?.[0];

          const certonaContentForOos = certonaContent && {
            ...certonaContent,
            container_id: certonaMiddleOos ?? certonaBottomOos ?? '',
          };

          const certonaContentBlock =
            certonaType === 'pencilpdp' ? certonaContentForOos : certonaContent;

          const isCertonaContentPresent =
            (useNewPersonalizationEngine &&
              certonaContentBlock?.container_id === 'partproduct_rr') ||
            (certonaContentBlock?.container_id &&
              certonaRecommendations?.some((recommendation) =>
                recommendation?.items?.length && Array.isArray(recommendation.scheme)
                  ? recommendation.scheme.includes(certonaContentBlock.container_id)
                  : recommendation.scheme === certonaContentBlock?.container_id,
              ));

          if (certonaContentBlock && isCertonaContentPresent) {
            return (
              <CMSSpacer
                key={index}
                shouldReduceSpacing={isProductDetailsPageV2Enabled}
                id={
                  getBlockId('certona_middle_content') ??
                  getBlockId('certona_bottom_content') ??
                  getBlockId('certona_content_2')
                }
              >
                <CMSCertona
                  content={certonaContentBlock as ContentStackCertona}
                  certonaRecommendations={certonaRecommendations}
                  onCertonaRecommendationClick={onCertonaRecommendationClick}
                  setAddedCertonaItemObject={setAddedCertonaItemObject}
                  onCertonaAddToCartClick={() => {
                    setCertonaRecommendationAddedToCart?.(true);
                  }}
                  onlyShowAvailableProductRecommendations={isPdpCertonaAddToCartEnabled}
                  LoadingSkeleton={ScrolledPartCardSkeleton}
                  checkContainerNameIncludes
                />
              </CMSSpacer>
            );
          }

          if (modularBlock.product_specification?.enable) {
            return (
              <CMSSpacer
                key={index}
                removeHorizontalSpacing
                removeVerticalSpacing={isProductDetailsPageV2Enabled}
                id={getBlockId('product_specification')}
              >
                {isPdpLowerV2Enabled ? (
                  <ProductDetailSpecificationV2
                    setWarrantyModal={setWarrantyModal}
                    warrantyModal={warrantyModal}
                    shouldShowLoadingSkeleton={shouldShowLoadingSkeleton}
                    oemPartNumber={oemPartNumber}
                  />
                ) : (
                  <ProductDetailSpecification
                    setWarrantyModal={setWarrantyModal}
                    warrantyModal={warrantyModal}
                    shouldOpenSpecsPanel={shouldOpenSpecsPanel}
                    setShouldOpenSpecsPanel={setShouldOpenSpecsPanel}
                    shouldShowLoadingSkeleton={shouldShowLoadingSkeleton}
                  />
                )}
              </CMSSpacer>
            );
          }

          if (modularBlock.product_details?.enable) {
            return (
              <CMSSpacer
                key={index}
                shouldReduceSpacing
                removeVerticalSpacing={isProductDetailsPageV2Enabled && { l: true }}
                removeHorizontalSpacing={isProductDetailsPageV2Enabled && isMobile}
                id={getBlockId('product_details')}
              >
                {ProductDetails}
              </CMSSpacer>
            );
          }

          if (modularBlock.breadcrumb?.enable) {
            return (
              <CMSSpacer key={index} shouldReduceSpacing id={getBlockId('breadcrumb')}>
                <CMSBreadcrumbs />
              </CMSSpacer>
            );
          }

          if (modularBlock.completethejob && (acesEnabled || partsProCtjConditions)) {
            return (
              <CMSSpacer
                key={index}
                removeVerticalSpacing={isProductDetailsPageV2Enabled}
                id={getBlockId('completethejob')}
              >
                <CMSCompleteTheJob
                  fulfillmentId={fulfillmentId}
                  shippingType={shippingType}
                  itemsToDisplay={
                    hasCustomCTJ && modularBlock.completethejob.items_to_display
                      ? modularBlock.completethejob.items_to_display
                      : undefined
                  }
                />
              </CMSSpacer>
            );
          }

          if (modularBlock.seo_comparision_chart?.enable && canRenderComparisonChart) {
            return (
              <CMSSpacer
                key={index}
                removeVerticalSpacing={isPdpLowerV2Enabled}
                id={getBlockId('seo_comparision_chart')}
              >
                <CMSSEOComparisonChart certonaRecommendations={certonaRecommendations} />
              </CMSSpacer>
            );
          }

          if (modularBlock.target_div?.target_div?.[0]) {
            return (
              <CMSSpacer key={index} id={getBlockId('target_div')}>
                <CMSTarget content={modularBlock.target_div.target_div[0]} />
              </CMSSpacer>
            );
          }

          if (
            enableBuyAgainOnPlpAndPdp &&
            buyAgainEndpointEnabled &&
            modularBlock.buy_again_container?.buy_again_container?.[0]
          ) {
            const buyAgainContainer = modularBlock.buy_again_container.buy_again_container[0];
            return (
              <div id={getBlockId('buy_again_container')} key={index}>
                <BuyAgainCandidatesWithFallback
                  limit={buyAgainContainer.product_buy_again_limit ?? 0}
                  title={buyAgainContainer.buy_again_title ?? ''}
                  dateRange={dateRange}
                  fallbackBanner={buyAgainContainer.fallback_banner?.[0]}
                  partTerminologyId={partTerminologyId}
                  partGroupId={partGroupId}
                  currentSkuId={currentSkuId}
                  SpacerComponent={CMSSpacer}
                  sessionStorageKey={'showPDPBuyAgainCandidates'}
                  isPendingData={isPending}
                />
              </div>
            );
          }

          if ('pencil_banner' in modularBlock && modularBlock.pencil_banner?.pencil_banner?.[0]) {
            return (
              <View
                padding={[4, 0]}
                key={index}
                {...(modularBlock.pencil_banner.pencil_banner[0].component_id && {
                  id: modularBlock.pencil_banner.pencil_banner[0].component_id,
                })}
              >
                <CMSPencilBanner content={modularBlock.pencil_banner.pencil_banner[0]} />
              </View>
            );
          }
        })}
      </STARCTypography>
    </View>
  );
};

export { CMSModularHandler };
